import type { Module, MutationTree } from 'vuex'
import { recipientActions } from './actions'
import type { RecipientState } from './types'
import type { MessageRecipient } from '@/api/recipients/types'
import type { RootState } from '@/store/modules/types'

export const initialRecipientState = (): RecipientState => ({
  recipients: [],
  recipientOids: {},
  recipientsOffset: 0,
  isFetchingRecipients: false,
  isNoMoreRecipients: false,
  hasFetchRecipientsFailed: false,
  isExportingRecipients: false,
})

function setRecipients(state: RecipientState, recipients: MessageRecipient[]) {
  state.recipients = recipients
  state.recipientOids = Object.fromEntries(recipients.map(r => [r.oid, true]))
}

// Existing oids are filtered out: recipients are ordered by sys_mtime, so a
// fan-message updating between two pages shifts the others and the same
// recipient could otherwise be returned twice (duplicate keys in the UI).
function concatRecipients(state: RecipientState, recipients: MessageRecipient[]) {
  const newRecipients = recipients.filter(r => !state.recipientOids[r.oid])
  newRecipients.forEach((r) => {
    state.recipientOids[r.oid] = true
  })
  state.recipients = state.recipients.concat(newRecipients)
}

export const recipientMutations: MutationTree<RecipientState> = {
  RESET_RECIPIENTS(state) {
    Object.assign(state, initialRecipientState())
  },
  SET_RECIPIENTS: setRecipients,
  CONCAT_RECIPIENTS: concatRecipients,
  // Starts a fetch: a reload empties the list right away so old rows never stay.
  START_RECIPIENTS_FETCH(state, reload: boolean) {
    if (reload) {
      setRecipients(state, [])
      state.isNoMoreRecipients = false
    }
    state.hasFetchRecipientsFailed = false
    state.isFetchingRecipients = true
  },
  RECIPIENTS_FETCHED(state, { recipients, reload, top }: { recipients: MessageRecipient[], reload: boolean, top: number }) {
    const previousCount = reload ? 0 : state.recipients.length
    if (reload) {
      setRecipients(state, recipients)
      state.recipientsOffset = 0
    }
    else {
      concatRecipients(state, recipients)
    }
    state.recipientsOffset += recipients.length
    // A short page is the last one; a page adding nothing new means the server only repeated known rows
    if (recipients.length < top || state.recipients.length === previousCount) {
      state.isNoMoreRecipients = true
    }
    state.isFetchingRecipients = false
  },
  RECIPIENTS_FETCH_FAILED(state) {
    state.hasFetchRecipientsFailed = true
    state.isFetchingRecipients = false
  },
  SET_IS_FETCHING_RECIPIENTS(state, isFetching: boolean) {
    state.isFetchingRecipients = isFetching
  },
  SET_IS_NO_MORE_RECIPIENTS(state, isNoMore: boolean) {
    state.isNoMoreRecipients = isNoMore
  },
  SET_HAS_FETCH_RECIPIENTS_FAILED(state, hasFailed: boolean) {
    state.hasFetchRecipientsFailed = hasFailed
  },
  SET_IS_EXPORTING_RECIPIENTS(state, isExporting: boolean) {
    state.isExportingRecipients = isExporting
  },
}

const recipientModule: Module<RecipientState, RootState> = {
  namespaced: true,
  state: initialRecipientState,
  actions: recipientActions,
  mutations: recipientMutations,
}

export default recipientModule
